/**
 * 選擇感知執行器
 * 處理需要玩家選擇的效果執行
 */
import type {
  GameState,
  Card,
  CardDefinition,
} from '../../types';
import type { ImmediateAction } from './EffectTriggerSystem';
import { ActionExecutor, type ExecutionResult, type ExecutionContext } from './ActionExecutor';
import {
  ChoiceManager,
  ChoiceValidator,
  getChoiceManager,
  type PendingChoice,
  type ChoiceResult,
  type ChoiceRequest,
  type TargetFilter,
} from './ChoiceSystem';

// ============================================================
// 執行狀態
// ============================================================

/**
 * 效果執行狀態
 */
export type EffectExecutionStatus =
  | 'completed'      // 執行完成
  | 'pending_choice' // 等待玩家選擇
  | 'failed';        // 執行失敗

/**
 * 選擇感知執行結果
 */
export interface ChoiceAwareResult extends ExecutionResult {
  status: EffectExecutionStatus;
  pendingChoice?: PendingChoice;
  executionId?: string;
}

/**
 * 暫停的效果執行
 */
interface PausedExecution {
  id: string;
  state: GameState;
  remainingActions: ImmediateAction[];
  context: Partial<ExecutionContext>;
  choiceId: string;
  results: ExecutionResult;
}

// ============================================================
// 選擇感知執行器
// ============================================================

/**
 * 選擇感知執行器
 * 在需要玩家選擇時暫停執行，等待選擇後繼續
 */
export class ChoiceAwareExecutor {
  private baseExecutor: ActionExecutor;
  private choiceManager: ChoiceManager;
  private validator: ChoiceValidator;
  private pausedExecutions: Map<string, PausedExecution> = new Map();
  private executionIdCounter = 0;

  constructor(definitions: Map<string, CardDefinition>) {
    this.baseExecutor = new ActionExecutor(definitions);
    this.choiceManager = getChoiceManager();
    this.validator = new ChoiceValidator(definitions);
  }

  /**
   * 執行動作列表（可能暫停等待選擇）
   */
  execute(
    state: GameState,
    actions: ImmediateAction[],
    context?: Partial<ExecutionContext>
  ): ChoiceAwareResult {
    let currentState = state;
    const allMessages: string[] = [];
    const allAffectedIds: string[] = [];
    
    for (let i = 0; i < actions.length; i++) {
      const action = actions[i];
      
      // 檢查動作是否需要選擇
      const choiceReq = this.checkActionRequiresChoice(currentState, action, context);
      
      if (choiceReq) {
        // 創建待決選擇
        const sourceCard = this.findSourceCard(currentState, action.sourceCardId);
        const pendingChoice = this.choiceManager.createChoice(
          choiceReq,
          sourceCard ?? { id: action.sourceCardId, definitionId: '', ownerId: state.activePlayerId, location: 'battlefield', orientation: 'upright' },
          this.getCardName(action.sourceCardId, context),
          state.activePlayerId
        );
        
        // 暫停執行
        const executionId = `exec_${String(Date.now())}_${String(this.executionIdCounter++)}`;
        this.pausedExecutions.set(executionId, {
          id: executionId,
          state: currentState,
          remainingActions: actions.slice(i),
          context: context ?? {},
          choiceId: pendingChoice.id,
          results: {
            state: currentState,
            success: true,
            messages: allMessages,
            affectedCardIds: allAffectedIds,
          },
        });
        
        return {
          state: currentState,
          success: true,
          messages: [...allMessages, `等待玩家選擇: ${pendingChoice.effectText}`],
          affectedCardIds: allAffectedIds,
          status: 'pending_choice',
          pendingChoice,
          executionId,
        };
      }
      
      // 執行動作
      const result = this.baseExecutor.execute(currentState, action, context);
      currentState = result.state;
      allMessages.push(...result.messages);
      allAffectedIds.push(...result.affectedCardIds);
      
      if (!result.success) {
        return {
          state: currentState,
          success: false,
          messages: allMessages,
          affectedCardIds: allAffectedIds,
          status: 'failed',
        };
      }
    }
    
    return {
      state: currentState,
      success: true,
      messages: allMessages,
      affectedCardIds: [...new Set(allAffectedIds)],
      status: 'completed',
    };
  }

  /**
   * 繼續被暫停的執行
   */
  continueExecution(
    executionId: string,
    choiceResult: ChoiceResult
  ): ChoiceAwareResult {
    const paused = this.pausedExecutions.get(executionId);
    if (!paused) {
      return {
        state: null as unknown as GameState,
        success: false,
        messages: ['找不到暫停的執行'],
        affectedCardIds: [],
        status: 'failed',
      };
    }
    
    const pendingChoice = this.choiceManager.getPendingChoice(paused.choiceId);
    if (!pendingChoice) {
      return {
        state: paused.state,
        success: false,
        messages: ['找不到待決選擇'],
        affectedCardIds: [],
        status: 'failed',
      };
    }
    
    // 驗證選擇
    const validation = this.validator.validate(paused.state, pendingChoice, choiceResult);
    if (!validation.valid) {
      return {
        state: paused.state,
        success: false,
        messages: [validation.error ?? '選擇無效'],
        affectedCardIds: [],
        status: 'failed',
      };
    }
    
    // 移除待決選擇
    this.choiceManager.removeChoice(paused.choiceId);
    
    // 根據選擇結果修改剩餘動作
    const modifiedActions = this.applyChoiceToActions(
      paused.remainingActions,
      pendingChoice,
      choiceResult
    );
    
    // 繼續執行
    this.pausedExecutions.delete(executionId);
    
    const result = this.execute(paused.state, modifiedActions, paused.context);
    
    return {
      ...result,
      messages: [...paused.results.messages, ...result.messages],
      affectedCardIds: [...paused.results.affectedCardIds, ...result.affectedCardIds],
    };
  }

  /**
   * 檢查動作是否需要選擇
   */
  private checkActionRequiresChoice(
    state: GameState,
    action: ImmediateAction,
    _context?: Partial<ExecutionContext>
  ): ChoiceRequest | null {
    // 檢查 customHandler 標記
    if (action.customHandler === 'requiresChoice') {
      return action.choiceRequest as ChoiceRequest;
    }
    
    // 目標選擇類型
    if (action.type === 'destroy' || action.type === 'buff' || action.type === 'debuff') {
      const selector = action.targetSelector;
      if (selector && this.requiresTargetSelection(state, selector, action)) {
        return {
          type: 'target',
          effectText: this.getActionDescription(action),
          filter: {
            side: selector.side,
            zone: selector.zone,
          },
          minCount: selector.count ?? 1,
          maxCount: selector.count ?? 1,
        };
      }
    }
    
    // 卡牌選擇類型（從牌庫/墓地選牌）
    if (action.type === 'summon' && action.targetSelector?.zone === 'deck') {
      const validCards = this.validator.getValidCards(
        state,
        state.activePlayerId,
        { side: 'self', zone: 'deck', ...action.targetSelector.filter }
      );
      
      if (validCards.length > (action.targetSelector.count ?? 1)) {
        return {
          type: 'card',
          effectText: '從牌庫選擇卡牌',
          filter: {
            side: 'self',
            zone: 'deck',
            ...action.targetSelector.filter,
          },
          minCount: action.targetSelector.count ?? 1,
          maxCount: action.targetSelector.count ?? 1,
        };
      }
    }
    
    return null;
  }

  /**
   * 是否需要目標選擇
   */
  private requiresTargetSelection(
    state: GameState,
    selector: ImmediateAction['targetSelector'],
    action: ImmediateAction
  ): boolean {
    if (!selector) return false;
    
    // 如果已經指定了目標，不需要選擇
    if (action.targetCardId) return false;
    
    // 如果是「所有」目標，不需要選擇
    if (selector.count === undefined || selector.count === 0) return false;
    
    // 計算可用目標數量
    const filter: TargetFilter = {
      side: selector.side,
      zone: selector.zone,
      ...selector.filter,
    };
    
    const validTargets = this.validator.getValidCards(state, state.activePlayerId, filter);
    
    // 如果可用目標數量大於需要的數量，需要選擇
    return validTargets.length > selector.count;
  }

  /**
   * 根據選擇結果修改動作
   */
  private applyChoiceToActions(
    actions: ImmediateAction[],
    choice: PendingChoice,
    result: ChoiceResult
  ): ImmediateAction[] {
    if (result.cancelled) {
      // 取消執行，跳過第一個動作
      return actions.slice(1);
    }
    
    switch (choice.type) {
      case 'option':
        return this.applyOptionChoice(actions, choice, result);
      case 'target':
      case 'card':
        return this.applyCardChoice(actions, result);
      case 'position':
        return this.applyPositionChoice(actions, result);
      default:
        return actions;
    }
  }

  private applyOptionChoice(
    actions: ImmediateAction[],
    choice: PendingChoice,
    result: ChoiceResult
  ): ImmediateAction[] {
    const selectedIndex = choice.options?.findIndex(o => o.id === result.selectedOptionId) ?? -1;
    
    // 根據選項索引選擇對應的動作
    // 假設動作按選項順序排列
    if (selectedIndex >= 0 && actions.length > selectedIndex) {
      return [actions[selectedIndex]];
    }
    
    return actions;
  }

  private applyCardChoice(
    actions: ImmediateAction[],
    result: ChoiceResult
  ): ImmediateAction[] {
    if (!result.selectedCardIds?.length) return actions;
    
    // 修改動作的目標
    const modifiedAction: ImmediateAction = {
      ...actions[0],
      targetCardId: result.selectedCardIds[0],
      targetCardIds: result.selectedCardIds,
    };
    
    return [modifiedAction, ...actions.slice(1)];
  }

  private applyPositionChoice(
    actions: ImmediateAction[],
    result: ChoiceResult
  ): ImmediateAction[] {
    if (!result.selectedPosition) return actions;
    
    const modifiedAction: ImmediateAction = {
      ...actions[0],
      targetPosition: result.selectedPosition,
    };
    
    return [modifiedAction, ...actions.slice(1)];
  }

  // ============================================================
  // 輔助方法
  // ============================================================

  private findSourceCard(state: GameState, cardId: string): Card | null {
    // 搜尋戰場
    for (const side of [state.battlefield.player1Side, state.battlefield.player2Side]) {
      for (const slot of [...side.frontLine, ...side.backLine]) {
        if (slot.unit?.id === cardId) return slot.unit;
      }
    }
    
    // 搜尋其他區域
    for (const player of [state.player1, state.player2]) {
      for (const zone of [player.hand, player.graveyard, player.deck]) {
        const card = zone.find(c => c.id === cardId);
        if (card) return card;
      }
    }
    
    return null;
  }

  private getCardName(cardId: string, context?: Partial<ExecutionContext>): string {
    if (!context?.definitions) return cardId;
    
    // 嘗試從 cardId 提取 definitionId
    const parts = cardId.split('_');
    for (let i = parts.length; i > 0; i--) {
      const possibleDefId = parts.slice(0, i).join('_');
      const def = context.definitions.get(possibleDefId);
      if (def) return def.nameZh || def.name;
    }
    
    return cardId;
  }

  private getActionDescription(action: ImmediateAction): string {
    switch (action.type) {
      case 'destroy':
        return '選擇要破壞的目標';
      case 'buff':
        return '選擇要強化的目標';
      case 'debuff':
        return '選擇要弱化的目標';
      case 'summon':
        return '選擇要召喚的卡牌';
      default:
        return '選擇目標';
    }
  }

  // ============================================================
  // 查詢方法
  // ============================================================

  /**
   * 取得暫停的執行
   */
  getPausedExecution(executionId: string): PausedExecution | undefined {
    return this.pausedExecutions.get(executionId);
  }

  /**
   * 是否有暫停的執行
   */
  hasPausedExecutions(): boolean {
    return this.pausedExecutions.size > 0;
  }

  /**
   * 取得玩家的待決選擇
   */
  getPlayerPendingChoices(playerId: string): PendingChoice[] {
    return this.choiceManager.getPlayerPendingChoices(playerId);
  }
}

// ============================================================
// 工廠函數
// ============================================================

export function createChoiceAwareExecutor(
  definitions: Map<string, CardDefinition>
): ChoiceAwareExecutor {
  return new ChoiceAwareExecutor(definitions);
}
